import { Link } from "react-router-dom";
import type { Booking, Vehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";
import { formatLongDate } from "@/lib/date";
import { countDaysLabel, isAwaitingPayment, isCancelled } from "@/lib/bookings";
import BookingStatusBadge from "@/components/base/BookingStatusBadge";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface BookingSummaryCardProps {
  booking: Booking;
  vehicle: Vehicle | null;
  viewer: "renter" | "host";
}

export default function BookingSummaryCard({ booking, vehicle, viewer }: BookingSummaryCardProps) {
  const image = vehicle?.images?.[0]?.url || FALLBACK_IMAGE;
  const title = vehicle ? vehicleTitle(vehicle.make, vehicle.model, vehicle.year) : "Your Gari car";
  const awaitingPayment = isAwaitingPayment(booking.status);
  const cancelled = isCancelled(booking.status);

  return (
    <div className="overflow-hidden rounded-2xl surface-card">
      <div className="flex flex-col gap-4 p-5 sm:flex-row">
        <Link
          to={vehicle ? `/vehicle/${vehicle.id}` : "/browse"}
          className="h-32 w-full shrink-0 overflow-hidden rounded-xl bg-background-100 sm:h-24 sm:w-36"
        >
          <img src={image} alt={`${title} booking`} title={`${title} — Gari booking`} className="h-full w-full object-cover object-top" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-foreground-500">Booking #{booking.id}</p>
              <h2 className="mt-1 truncate font-heading text-lg font-semibold text-foreground-950">{title}</h2>
            </div>
            <BookingStatusBadge status={booking.status} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              {formatLongDate(booking.start_date)} → {formatLongDate(booking.end_date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500"></i> {countDaysLabel(booking.days)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-map-pin-line text-primary-500"></i>
              <span className="truncate">{booking.pickup_location || vehicle?.city || "Kenya"}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-background-200/70 p-5 sm:grid-cols-2">
        <div className="rounded-xl bg-background-200/40 p-3.5">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground-500">
            <i className="ri-user-3-line"></i> Rider
          </p>
          <p className="mt-1 truncate text-sm font-medium text-foreground-950">{booking.renter_name || "Gari rider"}</p>
        </div>
        <div className="rounded-xl bg-background-200/40 p-3.5">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground-500">
            <i className="ri-hand-coin-line"></i> Host
          </p>
          <p className="mt-1 truncate text-sm font-medium text-foreground-950">{vehicle?.owner_name || "Gari host"}</p>
        </div>
      </div>

      <div className="space-y-2.5 border-t border-background-200/70 p-5">
        <div className="flex items-center justify-between text-sm text-foreground-600">
          <span>Subtotal</span>
          <span className="text-foreground-950">{formatKES(booking.subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-sm text-foreground-600">
          <span>Service fee</span>
          <span className="text-foreground-950">{formatKES(booking.service_fee)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-background-200/70 pt-3">
          <span className="font-heading text-base font-semibold text-foreground-950">Total</span>
          <span className="font-heading text-xl font-semibold text-primary-500">{formatKES(booking.total_price)}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-background-200/70 p-5">
        {vehicle && (
          <Link
            to={`/vehicle/${vehicle.id}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-roadster-line"></i> View car
          </Link>
        )}
        {viewer === "renter" && awaitingPayment && !cancelled && (
          <Link
            to={`/checkout/${booking.id}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-secure-payment-line"></i> Complete payment
          </Link>
        )}
        {viewer === "renter" && (
          <Link
            to="/trips"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-950"
          >
            <i className="ri-arrow-left-line"></i> All trips
          </Link>
        )}
        {viewer === "host" && (
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-950"
          >
            <i className="ri-arrow-left-line"></i> Dashboard
          </Link>
        )}
      </div>
    </div>
  );
}